// P1-12: Hook that issues a stable idempotency key for a user-submitted
// intent — typically the lifetime of an open modal or wizard. The key
// stays constant across re-renders and retry attempts inside the same
// intent so the backend collapses duplicate submissions to one result.
//
// Usage:
//   const idemKey = useIdempotencyKey();
//   await apiClient.withIdempotencyKey(idemKey).post(...);
//   // If the modal closes and a new one opens, useIdempotencyKey()
//   // generates a fresh key for the new intent.
//
// If you need to rotate within the same component (e.g., after a hard
// reset that the operator confirmed), call rotate() — for almost every
// modal you can simply remount the component to get a new key.

import { useCallback, useRef, useState } from 'react';
import { newIdempotencyKey } from './api.js';

export function useIdempotencyKey(): {
  key: string;
  rotate: () => string;
} {
  // useRef stays stable across re-renders. useState forces a re-render
  // when the operator explicitly rotates (rare).
  const initial = useRef<string | null>(null);
  if (initial.current === null) initial.current = newIdempotencyKey();
  const [version, setVersion] = useState(0);
  const rotate = useCallback(() => {
    initial.current = newIdempotencyKey();
    setVersion((v) => v + 1);
    return initial.current;
  }, []);
  // version is read so React doesn't warn about unused setter; the
  // re-render is the side-effect we want on rotate.
  void version;
  return { key: initial.current, rotate };
}
